// src/pages/monthlyFlux/MonthlyFlux.jsx

import React, { useState, useMemo } from 'react';
import { 
    Card, Table, Tag, Spin, Alert, Button, Space, 
    Row, Col, Statistic, Tooltip, Tabs, message, Empty
} from 'antd';
import {
    ReloadOutlined,
    RiseOutlined,
    FallOutlined,
    ShoppingOutlined,
    DollarOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    WarningOutlined
} from '@ant-design/icons';
import './monthlyFlux.scss';
import JournalHeader from '../journalHeader/JournalHeader';
import FilterJournalStock from '../filterJournalStock/FilterJournalStock';
import useMonthlyFluxData from './hooks/useMonthlyFluxData';

const { TabPane } = Tabs;

const MonthlyFlux = () => {
    const {
        months,              // ← Directement accessible
        loading,
        error,
        filters,
        totaux,
        coherence,
        indicateurs,
        periode,
        filtresAppliques,
        refreshWithFilters,
        resetFilters
    } = useMonthlyFluxData();

    const [activeTab, setActiveTab] = useState('table');

    // ✅ Log pour déboguer
    console.log('📊 MonthlyFlux - months:', months);
    console.log('📊 MonthlyFlux - totaux:', totaux);
    console.log('📊 MonthlyFlux - coherence:', coherence);

    // Colonnes du tableau
    const columns = useMemo(() => [
        {
            title: 'Mois',
            dataIndex: 'mois',
            key: 'mois',
            fixed: 'left',
            width: 180,
            render: (value, record) => (
                <Space>
                    <CalendarOutlined style={{ color: '#1677ff' }} />
                    <span style={{ fontWeight: 600 }}>{value}</span>
                    {record.est_coherent === false && (
                        <Tooltip title="Incohérence détectée">
                            <WarningOutlined style={{ color: '#faad14' }} />
                        </Tooltip>
                    )}
                    {record.est_coherent === true && (
                        <Tooltip title="Données cohérentes">
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: 'Stock Initial',
            dataIndex: 'stock_initial',
            key: 'stock_initial',
            align: 'center',
            render: (value) => (
                <span style={{ fontWeight: 600, color: '#595959' }}>
                    {value?.toLocaleString() || 0}
                </span>
            )
        },
        {
            title: 'Entrées',
            dataIndex: 'entrees',
            key: 'entrees',
            align: 'center',
            render: (value) => (
                <Tag color="success" style={{ fontWeight: 600, fontSize: 14 }}>
                    +{value?.toLocaleString() || 0}
                </Tag>
            )
        },
        {
            title: 'Sorties',
            dataIndex: 'sorties',
            key: 'sorties',
            align: 'center',
            render: (value) => (
                <Tag color="error" style={{ fontWeight: 600, fontSize: 14 }}>
                    -{value?.toLocaleString() || 0}
                </Tag>
            )
        },
        {
            title: 'Retours',
            dataIndex: 'retours',
            key: 'retours',
            align: 'center',
            render: (value) => (
                <Tag color="orange" style={{ fontWeight: 600, fontSize: 14 }}>
                    +{value?.toLocaleString() || 0}
                </Tag>
            )
        },
        {
            title: 'Stock Final',
            dataIndex: 'stock_final',
            key: 'stock_final',
            align: 'center',
            render: (value, record) => {
                const variation = record.variation || 0;
                const isPositive = variation >= 0;
                return (
                    <Tooltip title={variation !== 0 ? `Variation: ${isPositive ? '+' : ''}${variation}` : 'Pas de variation'}>
                        <Space>
                            <span style={{ fontWeight: 700, color: '#1677ff', fontSize: 16 }}>
                                {value?.toLocaleString() || 0}
                            </span>
                            {variation !== 0 && (
                                <Tag color={isPositive ? 'green' : 'red'} style={{ fontSize: 11 }}>
                                    {isPositive ? <RiseOutlined /> : <FallOutlined />}
                                    {isPositive ? '+' : ''}{variation}
                                </Tag>
                            )}
                        </Space>
                    </Tooltip>
                );
            }
        },
        {
            title: 'CA',
            dataIndex: 'ca',
            key: 'ca',
            align: 'right',
            render: (value) => (
                <span style={{ fontWeight: 700, color: '#1677ff', fontSize: 15 }}>
                    {value?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $
                </span>
            )
        },
        {
            title: 'Taux Rotation',
            dataIndex: 'taux_rotation',
            key: 'taux_rotation',
            align: 'center',
            render: (value) => {
                const numValue = Number(value) || 0;
                let color = 'red';
                let label = 'Faible';
                if (numValue > 70) { color = 'green'; label = 'Élevé'; }
                else if (numValue > 40) { color = 'orange'; label = 'Moyen'; }
                return (
                    <Tooltip title={`Taux de rotation: ${label}`}>
                        <Tag color={color} style={{ fontWeight: 600, fontSize: 13 }}>
                            {numValue}%
                        </Tag>
                    </Tooltip>
                );
            }
        }
    ], []);

    // Gestion des filtres
    const handleFilter = (newFilters) => {
        refreshWithFilters(newFilters);
    };

    // Gestion du rafraîchissement
    const handleRefresh = () => {
        refreshWithFilters({});
        message.success('Données rafraîchies');
    };

    // Gestion de la réinitialisation
    const handleReset = () => {
        resetFilters();
        message.info('Filtres réinitialisés');
    };

    // États de chargement et d'erreur
    if (loading && !months?.length) {
        return (
            <div className="page">
                <JournalHeader title="Flux mensuel" />
                <div className="page-content">
                    <div style={{ textAlign: 'center', padding: '80px 0' }}>
                        <Spin size="large" tip="Chargement des données..." />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page">
                <JournalHeader title="Flux mensuel" />
                <div className="page-content">
                    <Alert
                        message="Erreur de chargement"
                        description={error}
                        type="error"
                        showIcon
                        action={
                            <Button size="small" type="primary" onClick={handleRefresh}>
                                <ReloadOutlined /> Réessayer
                            </Button>
                        }
                    />
                </div>
            </div>
        );
    }

    // ✅ Affichage des filtres appliqués
    const filterTags = Object.entries(filtresAppliques || {})
        .filter(([key, value]) => value && value !== 'Toutes' && value !== 'Tous')
        .map(([key, value]) => (
            <Tag key={key} color="blue">
                {key.replace('_id', '').replace('_', ' ')}: {value}
            </Tag>
        ));

    return (
        <div className="page">
            <JournalHeader title="Flux mensuel" />

            <div className="page-content">
                {/* Filtres */}
                <FilterJournalStock 
                    onSearch={handleFilter} 
                    onReset={handleReset}
                    defaultValues={filters}
                />

                {/* Filtres appliqués */}
                {filterTags.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <span style={{ marginRight: 8, color: '#8c8c8c' }}>Filtres actifs:</span>
                        {filterTags}
                    </div>
                )}

                {/* KPIs */}
                <Row gutter={[16, 16]} className="monthly-kpis">
                    <Col xs={24} sm={12} md={6}>
                        <Card className="kpi-card" bordered={false}>
                            <Statistic
                                title="Stock initial"
                                value={totaux?.stock_initial_total || 0}
                                valueStyle={{ color: '#1677ff' }}
                                prefix={<ShoppingOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card className="kpi-card" bordered={false}>
                            <Statistic
                                title="Stock final"
                                value={totaux?.stock_final_total || 0}
                                valueStyle={{ color: '#1677ff' }}
                                prefix={<ShoppingOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card className="kpi-card" bordered={false}>
                            <Statistic
                                title="CA total"
                                value={totaux?.ca_total || 0}
                                valueStyle={{ color: '#52c41a' }}
                                prefix={<DollarOutlined />}
                                suffix="$"
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card className="kpi-card" bordered={false}>
                            <Statistic
                                title="Période"
                                value={`${periode?.nb_mois || 0} mois`}
                                valueStyle={{ color: '#722ed1' }}
                                prefix={<CalendarOutlined />}
                            />
                            {indicateurs && (
                                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                    Taux rotation moyen: {indicateurs.taux_rotation_moyen || 0}%
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>

                {/* Tableau et graphiques */}
                <Card 
                    bordered={false} 
                    className="monthly-table"
                    title="Évolution mensuelle du stock"
                    extra={
                        <Space>
                            <Button 
                                size="small" 
                                icon={<ReloadOutlined />} 
                                onClick={handleRefresh}
                                loading={loading}
                            >
                                Rafraîchir
                            </Button>
                        </Space>
                    }
                >
                    {months.length === 0 ? (
                        <Empty description="Aucune donnée disponible" />
                    ) : (
                        <Tabs activeKey={activeTab} onChange={setActiveTab}>
                            <TabPane tab="Tableau" key="table">
                                <Table
                                    columns={columns}
                                    dataSource={months}
                                    rowKey={(record) => record.mois || record.date_debut}
                                    bordered
                                    size="middle"
                                    pagination={false}
                                    scroll={{ x: 1100 }}
                                    loading={loading}
                                    rowClassName={(record) => 
                                        record.est_coherent === false ? 'row-incoherent' : ''
                                    }
                                    summary={() => {
                                        if (!totaux || !totaux.stock_initial_total) return null;
                                        return (
                                            <Table.Summary fixed>
                                                <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                                                    <Table.Summary.Cell index={0} align="center">
                                                        <Tag color="blue">TOTAUX</Tag>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={1} align="center">
                                                        {totaux.stock_initial_total?.toLocaleString() || 0}
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={2} align="center" style={{ color: '#52c41a' }}>
                                                        +{totaux.entrees_total?.toLocaleString() || 0}
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={3} align="center" style={{ color: '#ff4d4f' }}>
                                                        -{totaux.sorties_total?.toLocaleString() || 0}
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={4} align="center" style={{ color: '#faad14' }}>
                                                        +{totaux.retours_total?.toLocaleString() || 0}
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={5} align="center" style={{ color: '#1677ff' }}>
                                                        {totaux.stock_final_total?.toLocaleString() || 0}
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={6} align="right" style={{ color: '#52c41a' }}>
                                                        {totaux.ca_total?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={7} align="center">-</Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        );
                                    }}
                                />
                            </TabPane>
                            <TabPane tab="Graphique" key="chart">
                                <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
                                    <CalendarOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                                    <p>Graphique d'évolution du stock (à implémenter avec Chart.js ou Recharts)</p>
                                </div>
                            </TabPane>
                            <TabPane tab="Analyse" key="analysis">
                                <div style={{ padding: '20px 0' }}>
                                    <Row gutter={[16, 16]}>
                                        <Col span={24}>
                                            <Card size="small" title="Indicateurs clés">
                                                <Row gutter={16}>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="CA moyen par mois"
                                                            value={indicateurs?.ca_moyen_par_mois || 0}
                                                            suffix="$"
                                                            valueStyle={{ fontSize: 18 }}
                                                        />
                                                    </Col>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="Sorties moyennes par mois"
                                                            value={indicateurs?.sorties_moyennes_par_mois || 0}
                                                            valueStyle={{ fontSize: 18 }}
                                                        />
                                                    </Col>
                                                    <Col span={8}>
                                                        <Statistic
                                                            title="Taux de rotation moyen"
                                                            value={indicateurs?.taux_rotation_moyen || 0}
                                                            suffix="%"
                                                            valueStyle={{ fontSize: 18 }}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                        <Col span={24}>
                                            <Card size="small" title="Cohérence des données">
                                                <Row gutter={16}>
                                                    <Col span={12}>
                                                        <Statistic
                                                            title="Statut global"
                                                            value={coherence?.ok ? '✅ Cohérent' : '⚠️ Incohérent'}
                                                            valueStyle={{ 
                                                                color: coherence?.ok ? '#52c41a' : '#faad14',
                                                                fontSize: 20
                                                            }}
                                                        />
                                                    </Col>
                                                    <Col span={12}>
                                                        <Statistic
                                                            title="Mois incohérents"
                                                            value={coherence?.nb_mois_incoherents || 0}
                                                            valueStyle={{ 
                                                                color: coherence?.nb_mois_incoherents > 0 ? '#ff4d4f' : '#52c41a',
                                                                fontSize: 20
                                                            }}
                                                        />
                                                    </Col>
                                                </Row>
                                                {coherence?.details && (
                                                    <div style={{ marginTop: 16 }}>
                                                        {coherence.details
                                                            .filter(d => !d.est_coherent)
                                                            .map((d, i) => (
                                                                <Alert
                                                                    key={i}
                                                                    message={`${d.mois}: Incohérence détectée`}
                                                                    description={`Stock initial: ${d.stock_initial}, Entrées: ${d.entrees}, Sorties: ${d.sorties}, Stock calculé: ${d.stock_calcule}, Stock final: ${d.stock_final}, Écart: ${d.ecart}`}
                                                                    type="warning"
                                                                    showIcon
                                                                    style={{ marginBottom: 8 }}
                                                                    size="small"
                                                                />
                                                            ))}
                                                    </div>
                                                )}
                                            </Card>
                                        </Col>
                                    </Row>
                                </div>
                            </TabPane>
                        </Tabs>
                    )}
                </Card>

                {/* Footer avec informations */}
                <div style={{ marginTop: 16, fontSize: 12, color: '#8c8c8c', textAlign: 'center' }}>
                    <p>
                        Données basées sur {months?.length || 0} mois • 
                        Source: varianteproduit + reception + vente + mouvement_stock • 
                        Version V4
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MonthlyFlux;